/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect } from "react-redux";

/* Poolcorp Custom Code */
import { FormFieldIcon } from "@insite/mobius/FormField";
import { TYPOGRAPHY } from "@store/theme/design";
import { css } from "styled-components";
/* Poolcorp Custom Code */

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export const productUnitOfMeasureSelectStyles: SelectPresentationProps = {
    sizeVariant: "small",
    cssOverrides: {
        formInputWrapper: css`
            width: auto;
            margin: 0 !important;
            ${FormFieldIcon} {
                top: 0px;
            }
        `,
        formField: css`
            margin-top: 0;
            display: flex;
            flex-direction: row;
            align-items: center;
            width: auto;

            input {
                border-width: 2px;
            }
        `,
        inputSelect: css`
            padding: 0.5em;
            text-align: left;
            width: auto;
        `,
    },
    css: css`
        /* !important on next 3 lines: Using !important here was chosen to pursue instead of overriding the entirety of the style state on the relevant components. */
        border-radius: 0.5em !important;
        height: 2rem !important;
        border-width: 1px !important;
        font-size: ${TYPOGRAPHY.size.small};
        line-height: ${TYPOGRAPHY.lineHeight.small};
    `,
};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: { unitOfMeasures },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    },
    disabled,
    productSettings,
    labelOverride,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(productUnitOfMeasureSelectStyles, extendedStyles));

    /* Poolcorp Custom Code */
    const uomLabelStyles: TypographyPresentationProps = {
        size: TYPOGRAPHY.size.smallest,
        css: css`
            display: flex;
            align-items: center;
        `,
    };

    if (
        !productSettings?.alternateUnitsOfMeasure ||
        !unitOfMeasures ||
        unitOfMeasures.filter(uom => uom.unitOfMeasure).length < 1
    ) {
        return null;
    }

    if (unitOfMeasures.filter(uom => uom.unitOfMeasure).length === 1) {
        return <Typography {...uomLabelStyles}>{unitOfMeasure}</Typography>;
    }
    /* Poolcorp Custom Code */

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event.target.value);
    };

    return (
        <Select
            label={labelOverride ?? translate("U/M")}
            value={unitOfMeasure}
            onChange={uomChangeHandler}
            disabled={disabled}
            data-test-selector="product_unitOfMeasureSelect"
            {...styles}
            {...otherProps}
        >
            {unitOfMeasures.map(uom => (
                <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                    {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                    {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""}
                </option>
            ))}
        </Select>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
